import React from 'react'
import { renderRoutes } from 'react-router-config'
import { Redirect } from 'react-router-dom'

import { get, has, isArray } from 'lodash'

import {
  carePlanEnable$,
  patientDiseases$,
  patientProfileSelector,
  referralLicense$,
  soapLicense$,
  teleMedicineEnable$,
  urgentCareLicense$
} from 'src/store/modules/patient'
import {
  isAppointmentCallbackFieldsRequired$,
  isCareGiverCompanyGamificationEnabled$,
  isPatientCompanyGamificationEnabled$,
  isUserAdmin$,
  userAppointmentType$,
  userLicenseModule$,
  userPermissionByNameIsNone$,
  userPermissionByNameIsWrite$,
  userPermissions$
} from 'src/store/modules/user'

import LazyImport from 'src/HOC/LazyImport'
import withAuthentication from 'src/HOC/withAuthentication'
import withOutAuthentication from 'src/HOC/withOutAuthentication'
import AuthLayout from 'src/components/App/components/AuthLayout'
import SimpleLayout from 'src/components/App/components/SimpleLayout'
import { APPOINTMENT_TYPES_DROPDOWN } from 'src/components/UserModal/constants'
import {
  MODULE_LIST,
  PATIENT_SET_PRIMARY_DISEASE_MESSAGE,
  UUID_REGEXP,
  statusesByName
} from 'src/constants'
import NotFound from 'src/scenes/NotFound'
import { showInfoModal } from 'src/services/dialogs'
import { store } from 'src/store'
import { replaceKeyWords } from 'src/utils/String'

const getInfoMessage = (moduleName = '') => {
  switch (moduleName) {
    case MODULE_LIST.education:
      return replaceKeyWords(PATIENT_SET_PRIMARY_DISEASE_MESSAGE, {
        '{:îtem}': 'course'
      })

    case MODULE_LIST.carePlan:
      return replaceKeyWords(PATIENT_SET_PRIMARY_DISEASE_MESSAGE, {
        '{:îtem}': 'goal'
      })

    case MODULE_LIST.medication:
      return replaceKeyWords(PATIENT_SET_PRIMARY_DISEASE_MESSAGE, {
        '{:îtem}': 'medication'
      })

    case MODULE_LIST.measurement:
      return replaceKeyWords(PATIENT_SET_PRIMARY_DISEASE_MESSAGE, {
        '{:îtem}': 'measurement'
      })

    case MODULE_LIST.clinicalPathway:
      return replaceKeyWords(PATIENT_SET_PRIMARY_DISEASE_MESSAGE, {
        '{:îtem}': 'check-in'
      })

    case MODULE_LIST.soap:
      return replaceKeyWords(PATIENT_SET_PRIMARY_DISEASE_MESSAGE, {
        '{:îtem}': 'visit note'
      })

    default:
      return ''
  }
}

const checkIfModuleAvailable = (props) => {
  const { route } = props
  const {
    moduleName = '',
    relatedToDisease = false,
    routes = [],
    skipPendingRedirect = false
  } = route
  const reduxStore = store.getState()
  const licenseModule = userLicenseModule$(reduxStore)
  const patientProfile = patientProfileSelector(reduxStore)
  const diseases = patientDiseases$(reduxStore)
  const isPatientCompanyGamificationEnabled =
    isPatientCompanyGamificationEnabled$(reduxStore)
  const { uuid, enrolledProgramStatus } = patientProfile
  const hasPrimaryDisease = diseases.filter(({ isPrimary }) => isPrimary).length

  if (relatedToDisease && moduleName === MODULE_LIST.carePlan) {
    if (!hasPrimaryDisease) {
      showInfoModal({
        content: getInfoMessage(moduleName),
        okText: 'Ok'
      })

      return <Redirect to={`/patients/${uuid}/enrolled-program`} />
    }

    return renderRoutes(routes)
  }

  if (
    (moduleName !== MODULE_LIST.clinicalPathway &&
      relatedToDisease &&
      !hasPrimaryDisease) ||
    (!skipPendingRedirect &&
      enrolledProgramStatus === statusesByName.get('Pending'))
  ) {
    showInfoModal({
      content: getInfoMessage(moduleName),
      okText: 'Ok'
    })

    return <Redirect to={`/patients/${uuid}/enrolled-program`} />
  }

  if (
    moduleName &&
    moduleName !== MODULE_LIST.carePlan &&
    !licenseModule.filter(({ name }) => name === moduleName).length
  ) {
    const modules = [MODULE_LIST.chat, MODULE_LIST.broadcast]
    const url =
      modules.indexOf(moduleName) !== -1
        ? '/patients'
        : `/patients/${uuid}/demographics`

    return <Redirect to={url} />
  }

  if (moduleName === MODULE_LIST.chat) {
    const CommunicationScreen = LazyImport(() =>
      import(
        'src/scenes/CommunicationScreen' /* webpackChunkName: "CommunicationScreen" */
      )
    )

    return <CommunicationScreen {...props} />
  }

  if (
    moduleName === MODULE_LIST.gamification &&
    !isPatientCompanyGamificationEnabled
  ) {
    return <Redirect to={`/patients/${uuid}/demographics`} />
  }

  return renderRoutes(routes)
}

const checkTelemedicineEnable = (component) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)
  const teleMedicineEnable = teleMedicineEnable$(reduxStore)
  const telehealthEnable = get(
    userPermissions,
    ['telemedicine', 'permissions', 'read'],
    false
  )

  return (
    (teleMedicineEnable && telehealthEnable && component) || (
      <Redirect to="/not-found" />
    )
  )
}

const checkPermissionsIsRead = (
  component,
  permissionName = '',
  checkUserIsAdmin = false
) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)
  const isUserAdmin = isUserAdmin$(reduxStore)

  let readPermission = false

  if (checkUserIsAdmin && !isUserAdmin) {
    return <Redirect to="/not-found" />
  }

  if (isArray(permissionName)) {
    permissionName.forEach((name = '') => {
      if (!readPermission) {
        const permissions = get(userPermissions, [name, 'permissions'], {})

        readPermission = has(permissions, 'read')
          ? get(permissions, ['read'], false)
          : get(permissions, ['write'], false)
      }
    })
  } else {
    const permissions = get(
      userPermissions,
      [permissionName, 'permissions'],
      {}
    )

    readPermission = has(permissions, 'read')
      ? get(permissions, ['read'], false)
      : get(permissions, ['write'], false)

    if (permissionName === 'urgentCareReport' && readPermission) {
      const urgentCareLicence = urgentCareLicense$(reduxStore)

      if (!urgentCareLicence) return <Redirect to="/not-found" />
    }
  }

  return (readPermission && component) || <Redirect to="/not-found" />
}

const checkPermissionsIsReadOrAdmin = (component, permissionName = '') => {
  const reduxStore = store.getState()
  const isUserAdmin = isUserAdmin$(reduxStore)

  if (isUserAdmin) {
    return component
  }

  return checkPermissionsIsRead(component, permissionName)
}

const checkPremissionsIsWrite = (component, permission) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)

  const readPermission = get(
    userPermissions,
    [permission, 'permissions', 'write'],
    false
  )

  return (readPermission && component) || <Redirect to="/not-found" />
}

const checkUrgentCareEnable = (component) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)
  const urgentCareLicence = urgentCareLicense$(reduxStore)
  const urgentCarePermissions = get(
    userPermissions,
    ['urgentCare', 'permissions', 'read'],
    false
  )

  return (
    (urgentCareLicence && urgentCarePermissions && component) || (
      <Redirect to="/not-found" />
    )
  )
}

const checkReferralEnable = (component) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)
  const referralLicense = referralLicense$(reduxStore)
  const referralManagementIsRead = get(
    userPermissions,
    ['referralManagement', 'permissions', 'read'],
    false
  )

  return (
    (referralLicense && referralManagementIsRead && component) || (
      <Redirect to="/not-found" />
    )
  )
}

const checkSoapNoteReferenceTable = (component) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)
  const soapLicense = soapLicense$(reduxStore)
  const soapReferenceTable = get(
    userPermissions,
    ['soapReferenceTable', 'permissions', 'write'],
    false
  )

  return (
    (soapLicense && soapReferenceTable && component) || (
      <Redirect to="/not-found" />
    )
  )
}

const checkBillingDashboards = (component) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)
  const billingDashboardsPermissionEnable = get(
    userPermissions,
    ['billing', 'permissions', 'read'],
    false
  )

  return (
    (billingDashboardsPermissionEnable && component) || (
      <Redirect to="/not-found" />
    )
  )
}

const checkInterventionsPage = (component) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)
  const carePlanLicense = carePlanEnable$(reduxStore)
  const interventionsIsRead = get(
    userPermissions,
    ['interventions', 'permissions', 'read'],
    false
  )

  return (
    (carePlanLicense && interventionsIsRead && component) || (
      <Redirect to="/not-found" />
    )
  )
}

const checkQualityMeasurementsEnable = (component) => {
  const reduxStore = store.getState()
  const userPermissions = userPermissions$(reduxStore)
  const carePlanLicense = carePlanEnable$(reduxStore)
  const qualityMeasurementsIsRead = get(
    userPermissions,
    ['qualityMeasurementDashboard', 'permissions', 'read'],
    false
  )

  return (
    (carePlanLicense && qualityMeasurementsIsRead && component) || (
      <Redirect to="/not-found" />
    )
  )
}

// const ActivityLog = LazyImport(() =>
//   import(
//     'src/scenes/Patient/scenes/PatientActivityLog' /* webpackChunkName: "PatientActivityLog" */
//   )
// )

// const checkPatientStatus =
//   (moduleName = '') =>
//   (props) => {
//     const reduxStore = store.getState()
//     const patientProfile = patientProfileSelector(reduxStore)
//     const { uuid, problems = [], enrolledProgramStatus } = patientProfile
//     const hasPrimaryDisease = problems.filter(
//       ({ isPrimary }) => isPrimary
//     ).length

//     if (
//       enrolledProgramStatus === statusesByName.get('Pending') ||
//       (!hasPrimaryDisease && moduleName !== 'patientActivityLog')
//     ) {
//       return <Redirect to={`/patients/${uuid}/enrolled-program`} />
//     }

//     switch (moduleName) {
//       case 'patientActivityLog':
//         return <ActivityLog {...props} />

//       case 'patientExternalDocuments':
//         return <ExternalDocuments {...props} />

//       default:
//         return <Redirect to={`/patients/${uuid}/dashboard`} />
//     }
//   }
//
const checkIfModuleIsNull = (component, moduleName) => {
  const reduxStore = store.getState()

  const userPermissionByNameIsNone =
    userPermissionByNameIsNone$(moduleName)(reduxStore)

  return (
    (!userPermissionByNameIsNone && component) || <Redirect to="/not-found" />
  )
}

const checkIfCaregiverModuleAvailable = (props) => {
  const { route } = props
  const { moduleName = '' } = route
  const reduxStore = store.getState()
  const caregiverUuid = get(props, ['match', 'params', 'caregiverUuid'], null)

  switch (moduleName) {
    case 'CaregiverGamification': {
      const isCareGiverCompanyGamificationEnabled =
        isCareGiverCompanyGamificationEnabled$(reduxStore)
      const patientGamificationIsRead =
        userPermissionByNameIsWrite$('gamification')(reduxStore)
      const CaregiverGamification = LazyImport(() =>
        import(
          'src/scenes/Caregiver/scenes/CaregiverGamification' /* webpackChunkName: "CaregiverGamification" */
        )
      )

      return patientGamificationIsRead &&
        isCareGiverCompanyGamificationEnabled ? (
        <CaregiverGamification {...props} />
      ) : (
        <Redirect to={`/configuration/caregivers/${caregiverUuid}/profile`} />
      )
    }

    default:
      return <Redirect to={`/patients/${caregiverUuid}/demographics`} />
  }
}

const getDefaultRoute = (...props) => {
  const reduxStore = store.getState()
  const userAppointmentType = userAppointmentType$(reduxStore)
  const userPermissions = userPermissions$(reduxStore)
  const isAppointmentCallbackFieldsRequired =
    isAppointmentCallbackFieldsRequired$(reduxStore)
  const urgentCareLicence = urgentCareLicense$(reduxStore)
  const urgentCarePermissions = get(
    userPermissions,
    ['urgentCare', 'permissions', 'read'],
    false
  )
  const companyDashboardPermission = get(
    userPermissions,
    ['companyDashboard', 'permissions', 'read'],
    false
  )

  const urgentCarePermission =
    isAppointmentCallbackFieldsRequired &&
    userAppointmentType === APPOINTMENT_TYPES_DROPDOWN[1].value &&
    urgentCareLicence &&
    urgentCarePermissions

  let redirectUrl = urgentCarePermission
    ? 'dashboard/urgent-care-dashboard'
    : 'dashboard/company-dashboard'

  if (!companyDashboardPermission && !urgentCarePermission) {
    redirectUrl = '/patients'
  }

  return <Redirect to={redirectUrl} />
}

const checkUrgentCareLisence = (component) => {
  const reduxStore = store.getState()
  const urgentCareLicence = urgentCareLicense$(reduxStore)

  return urgentCareLicence ? component : <Redirect to="/not-found" />
}

const OutOfRangeRpm = LazyImport(() =>
  import(
    'src/scenes/portal/reports/OutOfRangeRpm' /* webpackChunkName: "OutOfRangeRpm" */
  )
)

export const carePortalRoutes = [
  {
    path: '/auth',
    component: withOutAuthentication(AuthLayout),
    routes: [
      {
        path: '/auth/login',
        component: LazyImport(() =>
          import('src/scenes/Auth/scenes/Login' /* webpackChunkName: "Login" */)
        )
      },
      {
        path: '/auth/two-factor-auth',
        component: LazyImport(() =>
          import(
            'src/scenes/Auth/scenes/TwoFactorAuth' /* webpackChunkName: "Login" */
          )
        )
      },
      {
        path: '/auth/set-password',
        component: LazyImport(() =>
          import(
            'src/scenes/Auth/scenes/SetPassword' /* webpackChunkName: "Set Password" */
          )
        )
      },
      {
        path: '/auth/forgot-password',
        component: LazyImport(() =>
          import(
            'src/scenes/Auth/scenes/ForgotPassword' /* webpackChunkName: "Forgot Password" */
          )
        )
      },
      {
        path: '/auth/restore-password',
        component: LazyImport(() =>
          import(
            'src/scenes/Auth/scenes/ForgotPassword' /* webpackChunkName: "Forgot Password" */
          )
        )
      },
      {
        path: '/auth/reset-password',
        component: LazyImport(() =>
          import(
            'src/scenes/Auth/scenes/ResetPassword' /* webpackChunkName: "Reset Password" */
          )
        )
      },
      {
        path: '/auth',
        component: () => <Redirect to="/auth/login" />
      }
    ]
  },
  {
    path: '/pdf',
    component: ({ route }) => renderRoutes(route.routes),
    routes: [
      {
        exact: true,
        path: '/pdf/care-plan-report',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/CarePlanReport' /* webpackChunkName: "CarePlanReportPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/care-plan-report-emr',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/CarePlanReportEmr' /* webpackChunkName: "CarePlanReportPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/care-plan-review',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/CarePlanReportReview' /* webpackChunkName: "CarePlanReportReviewPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/external-referral-report',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/PatientExternalReferralReport' /* webpackChunkName: "FaceSheetPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/facesheet-report',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/FaceSheet' /* webpackChunkName: "FaceSheetPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/patient-consent',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/PatientConsent' /* webpackChunkName: "PatientConsentPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/durable-medical-equipment-order',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/DurableMedicalEquipmentOrder' /* webpackChunkName: "DmeOrderPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/prescription',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/Prescription' /* webpackChunkName: "PrescriptionPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/external-referral',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/ExternalReferral' /* webpackChunkName: "ExternalReferralPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/diagnostic-order',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/DiagnosticOrder' /* webpackChunkName: "DiagnosticOrderPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/visit',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/VisitNote' /* webpackChunkName: "VisitPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/visit-full-assessments',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/VisitNoteFullAssessments' /* webpackChunkName: "VisitFullAssessmentsPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/patient-document/oru',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/PatientDocumentOru' /* webpackChunkName: "PatientDocumentOruPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/rpm-patient-report',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/PatientMeasurementReport' /* webpackChunkName: "PatientMeasurementReportPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/rpm-patient-report-new',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/PatientMeasurementReportNew' /* webpackChunkName: "PatientMeasurementReportNewPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/daily-facility-measurements',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/DailyFacilityMeasurements' /* webpackChunkName: "DailyFacilityMeasurements" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/activity-log',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/ActivityLogReport' /* webpackChunkName: "ActivityLogReport" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/activity-log-v2',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/ActivityLogV2Report' /* webpackChunkName: "ActivityLogV2Report" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/patient-verbal-consent',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/PatientVerbalConsent' /* webpackChunkName: "PatientVerbalConsent" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/patient-assessment',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/AssessmentReport' /* webpackChunkName: "AssessmentReportPDF" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/clinical-risk-dashboard',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/ClinicalRiskDashboardReport' /* webpackChunkName: "ClinicalRiskDashboardReport" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/clinical-risk-report',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/ClinicalRiskPatientReport' /* webpackChunkName: "ClinicalRiskPatientReport" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/care-gap-report',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/CareGapReport' /* webpackChunkName: "CareGapReportPdf" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/care-gap-facility-report',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/CareGapFacilityReport' /* webpackChunkName: "CareGapFacilityReportPdf" */
            )
          )
        )
      },
      {
        exact: true,
        path: '/pdf/chart-review-pdf',
        component: withOutAuthentication(
          LazyImport(() =>
            import(
              'src/scenes/ReportsPDF/ChartReviewPdf' /* webpackChunkName: "ChartReviewPdf" */
            )
          )
        )
      }
    ]
  },
  {
    path: '/referral',
    component: ({ route }) => renderRoutes(route.routes),
    routes: [
      {
        path: '/referral/:referralProvider',
        component: LazyImport(() =>
          import(
            'src/scenes/Referral/scenes/ReferralAddPage' /* webpackChunkName: "ReferralAddPage" */
          )
        )
      }
    ]
  },
  {
    path: '/telemedicine/join-anonymously/:uuid',
    component: withOutAuthentication(
      LazyImport(() =>
        import(
          'src/scenes/Actions/Telehealth/AnonVideoConference' /* webpackChunkName: "AnonVideoConference" */
        )
      )
    )
  },
  {
    path: '/urgent-care/join-anonymously/:uuid',
    component: withOutAuthentication(
      LazyImport(() =>
        import(
          'src/scenes/Actions/Telehealth/UrgentCareAnonConference' /* webpackChunkName: "UrgentCareAnonConference" */
        )
      )
    )
  },
  {
    path: '/communication',
    component: withAuthentication(SimpleLayout),
    routes: [
      {
        exact: true,
        path: '/communication',
        moduleName: MODULE_LIST.chat,
        component: checkIfModuleAvailable
      }
    ]
  },
  {
    path: '/patient-consent-form/:patientConsentUuid',
    component: withOutAuthentication(
      LazyImport(() =>
        import(
          'src/scenes/Reports/PatientConsentForm' /* webpackChunkName: "PatientConsentForm" */
        )
      )
    )
  },
  {
    path: '/',
    component: withAuthentication(
      LazyImport(() =>
        import(
          'src/components/App/components/MainLayout' /* webpackChunkName: "MainLayout" */
        )
      )
    ),
    routes: [
      {
        exact: true,
        path: '/',
        component: getDefaultRoute
      },
      {
        path: '/patients',
        component: ({ route }) => renderRoutes(route.routes),
        routes: [
          {
            path: '/patients/:id',
            component: LazyImport(() =>
              import('src/scenes/Patient' /* webpackChunkName: "Patient" */)
            ),
            routes: [
              {
                path: '/patients/:id/demographics',
                component: LazyImport(() =>
                  import(
                    'src/scenes/Patient/scenes/PatientDemographics' /* webpackChunkName: "PatientDemographics" */
                  )
                )
              },
              {
                path: `/patients/:patientUuid/visits`,
                moduleName: MODULE_LIST.soap,
                skipPendingRedirect: true,
                component: checkIfModuleAvailable,
                routes: [
                  {
                    path: '/patients/:patientUuid/visits',
                    exact: true,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientVisitList' /* webpackChunkName: "PatientVisitList" */
                          )
                        )(),
                        'soapNotes'
                      )
                  },
                  {
                    exact: true,
                    path: '/patients/:patientUuid/visits/:visitUuid',
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientVisitNote' /* webpackChunkName: "PatientVisitNote" */
                          )
                        )(),
                        'soapNotes'
                      )
                  }
                ]
              },
              {
                path: '/patients/:id/order-management',
                component: () =>
                  checkPermissionsIsReadOrAdmin(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientOrderManagement' /* webpackChunkName: "PatientOrderManagement" */
                      )
                    )(),
                    [
                      'patientExternalReferralManagement',
                      'patientPrescriptionManagement',
                      'patientDurableMedicalEquipmentOrderManagement',
                      'patientDiagnosticOrderManagement'
                    ]
                  )
              },
              {
                path: '/patients/:id/dashboard',
                component: () =>
                  checkIfModuleIsNull(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientDashboard' /* webpackChunkName: "PatientDashboard" */
                      )
                    )(),
                    'patientDashboard'
                  )
              },
              {
                path: '/patients/:id/enrolled-program',
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientEnrolledProgram' /* webpackChunkName: "PatientEnrolledProgram" */
                      )
                    )(),
                    'enrolledProgram'
                  )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/chart-review/:chartReviewUuid(${UUID_REGEXP})`,
                exact: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientChartReview' /* webpackChunkName: "PatientChartReview" */
                      )
                    )(),
                    'chartReview'
                  )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/chart-review/:chartReviewUuid(${UUID_REGEXP})`,
                exact: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientChartReview' /* webpackChunkName: "PatientChartReview" */
                      )
                    )(),
                    'chartReview'
                  )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/chart-review`,
                exact: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientChartReview/ChartReviewList' /* webpackChunkName: "ChartReviewList" */
                      )
                    )(),
                    'chartReview'
                  )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/medication`,
                moduleName: MODULE_LIST.medication,
                exact: true,
                relatedToDisease: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientMedicationManagement' /* webpackChunkName: "PatientMedicationManagement" */
                      )
                    )(),
                    'medication'
                  ),
                routes: [
                  {
                    exact: true,
                    path: `/patients/:patientUuid(${UUID_REGEXP})/medication`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientMedicationManagement' /* webpackChunkName: "PatientMedicationManagement" */
                          )
                        )(),
                        'medication'
                      )
                  }
                ]
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/medication/edit/:uuid(${UUID_REGEXP})`,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientMedicationManagement/scenes/EditMedication' /* webpackChunkName: "EditMedication" */
                      )
                    )(),
                    'medicationManagement'
                  )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/medication/add`,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientMedicationManagement/scenes/AddMedication' /* webpackChunkName: "AddMedication" */
                      )
                    )(),
                    'medicationManagement'
                  )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/measurements`,
                moduleName: MODULE_LIST.measurement,
                component: checkIfModuleAvailable,
                routes: [
                  {
                    exact: true,
                    path: `/patients/:patientUuid(${UUID_REGEXP})/measurements`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientMeasurements' /* webpackChunkName: "PatientMeasurements" */
                          )
                        )(),
                        'measurement'
                      )
                  },
                  {
                    path: `/patients/:patientUuid(${UUID_REGEXP})/measurements/add-measurement`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientMeasurements/scenes/AddMeasurement' /* webpackChunkName: "AddMeasurement" */
                          )
                        )(),
                        'measurementManagement'
                      )
                  },
                  {
                    path: `/patients/:patientUuid(${UUID_REGEXP})/measurements/edit-measurement/:uuid(${UUID_REGEXP})`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientMeasurements/scenes/EditMeasurement' /* webpackChunkName: "EditMeasurement" */
                          )
                        )(),
                        'measurementManagement'
                      )
                  }
                ]
              },
              {
                path: '/patients/:id/education',
                moduleName: MODULE_LIST.education,
                exact: true,
                relatedToDisease: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientEducation' /* webpackChunkName: "PatientCourses" */
                      )
                    )(),
                    'education'
                  ),
                routes: [
                  {
                    path: '/patients/:id/education/add-course',
                    component: () =>
                      checkPremissionsIsWrite(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientEducation/scenes/AddNewCourse' /* webpackChunkName: "PatientAddNewCourse" */
                          )
                        )(),
                        'education'
                      )
                  }
                ]
              },
              {
                path: '/patients/:id/education/add-course',
                component: () =>
                  checkPremissionsIsWrite(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientEducation/scenes/AddNewCourse' /* webpackChunkName: "PatientAddNewCourse" */
                      )
                    )(),
                    'education'
                  )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/clinical-pathways`,
                moduleName: MODULE_LIST.clinicalPathway,
                relatedToDisease: true,
                component: checkIfModuleAvailable,
                routes: [
                  {
                    exact: true,
                    path: `/patients/:patientUuid(${UUID_REGEXP})/clinical-pathways`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientClinicalPathways' /* webpackChunkName: "PatientClinicalPathways" */
                          )
                        )(),
                        'clinicalPathway'
                      )
                  },
                  {
                    path: `/patients/:patientUuid(${UUID_REGEXP})/clinical-pathways/add-pathway`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientClinicalPathways/scenes/AddPathway' /* webpackChunkName: "AddPathway" */
                          )
                        )(),
                        'clinicalPathwayManagement'
                      )
                  },
                  {
                    path: `/patients/:patientUuid(${UUID_REGEXP})/clinical-pathways/edit-pathway/:uuid(${UUID_REGEXP})`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientClinicalPathways/scenes/EditPathway' /* webpackChunkName: "EditPathway" */
                          )
                        )(),
                        'clinicalPathwayManagement'
                      )
                  },
                  {
                    path: `/patients/:patientUuid(${UUID_REGEXP})/clinical-pathways/view-pathway/:uuid(${UUID_REGEXP})`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientClinicalPathways/scenes/ViewPathway' /* webpackChunkName: "ViewPathway" */
                          )
                        )(),
                        'clinicalPathwayManagement'
                      )
                  }
                ]
              },
              {
                path: '/patients/:id/activity-log',
                component: () =>
                  checkIfModuleIsNull(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientActivityLog' /* webpackChunkName: "PatientActivityLog" */
                      )
                    )(),
                    'activityLog'
                  )
              },
              {
                path: '/patients/:id/gamification',
                moduleName: MODULE_LIST.gamification,
                component: checkIfModuleAvailable,
                routes: [
                  {
                    path: '/patients/:id/gamification',
                    exact: true,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientGamification' /* webpackChunkName: "PatientGamification" */
                          )
                        )(),
                        'gamification'
                      )
                  }
                ]
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/assessments`,
                component: LazyImport(() =>
                  import('src/scenes/Patient/scenes/PatientAssessments')
                )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/care-plan`,
                moduleName: MODULE_LIST.carePlan,
                relatedToDisease: true,
                component: checkIfModuleAvailable,
                routes: [
                  {
                    exact: true,
                    path: `/patients/:patientUuid(${UUID_REGEXP})/care-plan`,
                    component: () =>
                      checkPermissionsIsRead(
                        LazyImport(() =>
                          import(
                            'src/scenes/Patient/scenes/PatientCarePlan/scenes/PatientCarePlan' /* webpackChunkName: "PatientCarePlan" */
                          )
                        )(),
                        'careplan'
                      )
                  }
                ]
              },
              {
                exact: true,
                path: `/patients/:patientUuid(${UUID_REGEXP})/chart-review/:chartReviewUuid(${UUID_REGEXP})`,
                component: LazyImport(() =>
                  import('src/scenes/Patient/scenes/PatientChartReview')
                )
              },
              {
                exact: true,
                path: `/patients/:patientUuid(${UUID_REGEXP})/chart-review`,
                component: LazyImport(() =>
                  import(
                    'src/scenes/Patient/scenes/PatientChartReview/ChartReviewList'
                  )
                )
              },
              {
                path: `/patients/:patientUuid(${UUID_REGEXP})/external-documents`,
                component: () =>
                  checkPermissionsIsRead(
                    // checkPatientStatus('patientExternalDocuments')(),
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientExternalDocuments' /* webpackChunkName: "PatientExternalDocuments" */
                      )
                    )(),
                    'externalDocument'
                  )
              },
              {
                exact: true,
                path: `/patients/:patientUuid(${UUID_REGEXP})/medical-devices`,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Patient/scenes/PatientMedicalDevices' /* webpackChunkName: "PatientMedicalDevices" */
                      )
                    )(),
                    ['medicalDevicesAndOrders', 'enrolledProgram']
                  )
              },
              {
                exact: true,
                path: '/patients/:patientUuid/actions',
                component: LazyImport(() =>
                  import(
                    'src/scenes/Patient/scenes/PatientActions' /* webpackChunkName: "PatientActions" */
                  )
                )
              },
              {
                path: '/patients/:id',
                component: ({ match }) => (
                  <Redirect to={`/patients/${match.params.id}/demographics`} />
                )
              }
            ]
          },
          {
            path: '/patients',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/PatientSearch' /* webpackChunkName: "PatientSearch" */
                  )
                )(),
                'patientSearch'
              )
          }
        ]
      },
      {
        path: '/referrals',
        component: ({ route }) => renderRoutes(route.routes),
        routes: [
          {
            path: '/referrals/referral-import',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Referral/scenes/ReferralImport' /* webpackChunkName: "Referrals" */
                  )
                )(),
                'referralImport'
              )
          },
          {
            path: '/referrals/:uuid',
            component: () =>
              checkReferralEnable(
                LazyImport(() =>
                  import(
                    'src/scenes/Referral/scenes/ReferralInfo' /* webpackChunkName: "Referral" */
                  )
                )()
              )
          },
          {
            path: '/referrals',
            component: () =>
              checkReferralEnable(
                LazyImport(() =>
                  import(
                    'src/scenes/Referral/scenes/ReferralSearch' /* webpackChunkName: "Referrals" */
                  )
                )()
              )
          }
        ]
      },
      {
        path: '/dashboard',
        component: ({ route }) => renderRoutes(route.routes),
        routes: [
          {
            exact: true,
            path: '/dashboard/monitoring-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/MonitoringDashboard' /* webpackChunkName: "MonitoringDashboard" */
                  )
                )(),
                'monitoringDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/monitoring-dashboard-beta',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/dashboard/monitoring' /* webpackChunkName: "MonitoringDashboardBeta" */
                  )
                )(),
                'monitoringDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/rpm-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/dashboard/activityDashboardRpm' /* webpackChunkName: "ActivityDashboardRpm" */
                  )
                )(),
                'rpmDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/patient-activity-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/PatientActivity' /* webpackChunkName: "PatientActivity" */
                  )
                )(),
                'patientActivityDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/performance-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/PerformanceDashboard' /* webpackChunkName: "PerformanceDashboard" */
                  )
                )(),
                'performanceDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/operational-dashboard/summary',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/Operational/Summary' /* webpackChunkName: "OperationalSummary" */
                  )
                )(),
                'operationalDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/operational-dashboard/grouped',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/Operational/Grouped' /* webpackChunkName: "OperationalGrouped" */
                  )
                )(),
                'operationalDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/operational-dashboard/details',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/Operational/Details' /* webpackChunkName: "OperationalDetails" */
                  )
                )(),
                'operationalDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/action-hub',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/TaskDashboard' /* webpackChunkName: "TasksDashboard" */
                  )
                )(),
                'taskDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/referral-dashboard',
            component: () =>
              checkReferralEnable(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/ReferralDashboard' /* webpackChunkName: "ReferralDashboard" */
                  )
                )()
              )
          },
          {
            exact: true,
            path: '/dashboard/quality-measurement/dashboard',
            component: () =>
              checkQualityMeasurementsEnable(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/QualityMeasurementDashboard' /* webpackChunkName: "QualityMeasurementDashboard" */
                  )
                )()
              )
          },
          {
            exact: true,
            path: '/dashboard/eproms-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/Eproms/scenes/Dashboard' /* webpackChunkName: "EpromsDashboard" */
                  )
                )(),
                'epromDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/eproms-details',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/Eproms/scenes/Details' /* webpackChunkName: "EpromsDetails" */
                  )
                )(),
                'epromDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/risk-mitigation-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/RiskMitigation' /* webpackChunkName: "RiskMitigationDashboard" */
                  )
                )(),
                'riskMitigationDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/urgent-care-dashboard',
            component: () =>
              checkUrgentCareLisence(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/UrgentCareDashboard' /* webpackChunkName: "UrgentCareDashboard" */
                  )
                )()
              )
          },
          // {
          //   path: '/dashboard/risk-stratifications',
          //   component: ({ route }) => renderRoutes(route.routes),
          //   routes: [
          //     {
          //       exact: true,
          //       path: '/dashboard/risk-stratifications/patient/:patientUuid/:reportDate',
          //       component: () =>
          //         checkPermissionsIsRead(
          //           LazyImport(() =>
          //             import(
          //               'src/scenes/RiskStratification/scenes/PatientReport' /* webpackChunkName: "RiskStratificationPatientReport" */
          //             )
          //           )(),
          //           'clinicalRiskDashboard'
          //         )
          //     },
          //     {
          //       path: '/dashboard/risk-stratifications',
          //       component: () =>
          //         checkPermissionsIsRead(
          //           LazyImport(() =>
          //             import(
          //               'src/scenes/RiskStratification/scenes/Dashboard' /* webpackChunkName: "RiskStratificationDashboard" */
          //             )
          //           )(),
          //           'clinicalRiskDashboard'
          //         )
          //     }
          //   ]
          // },

          {
            exact: true,
            path: '/dashboard/company-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/CompanyDashboard' /* webpackChunkName: "CompanyDashboard" */
                  )
                )(),
                'companyDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/executive-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Dashboard/ExecutiveDashboard' /* webpackChunkName: "ExecutiveDashboard" */
                  )
                )(),
                'executiveDashboard'
              )
          },
          {
            exact: true,
            path: '/dashboard/executive-dashboard-v2',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/dashboard/executiveDashboard' /* webpackChunkName: "ExecutiveDashboardV2" */
                  )
                )(),
                'executiveDashboardV2'
              )
          },
          {
            path: '*',
            component: () => <Redirect to="/not-found" />
          }
        ]
      },
      {
        path: '/actions',
        component: ({ route }) => renderRoutes(route.routes),
        routes: [
          {
            exact: true,
            path: '/actions/alerts',
            component: LazyImport(() =>
              import('src/scenes/Alerts' /* webpackChunkName: "Alerts" */)
            )
          },
          {
            exact: true,
            path: '/actions/alerts/dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/AlertsDashboard' /* webpackChunkName: "AlertsDashboard" */
                  )
                )(),
                'alertDashboard'
              )
          },
          {
            exact: true,
            path: `/actions/alerts/:uuid(${UUID_REGEXP})`,
            component: LazyImport(() =>
              import('src/scenes/Alerts' /* webpackChunkName: "Alert" */)
            )
          },
          {
            exact: true,
            path: '/actions/cocm-registry',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/CoCMRegestry' /* webpackChunkName: "CoCMRegestry" */
                  )
                )(),
                'cocmRegistryDashboard'
              )
          },
          {
            exact: true,
            path: '/actions/tcm-dashboard',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/TCMDashboard' /* webpackChunkName: "TCMDashboard" */
                  )
                )(),
                'tcmDashboard'
              )
          },
          {
            exact: true,
            path: '/actions/new-billing-batch',
            component: LazyImport(() =>
              import(
                'src/scenes/Actions/Billing/BillingBatchGrid' /* webpackChunkName: "BillingBatchGrid" */
              )
            )
          },
          {
            exact: true,
            path: '/actions/batch-grid',
            component: LazyImport(() =>
              import(
                'src/scenes/Actions/Billing/BatchGrid' /* webpackChunkName: "BatchGrid" */
              )
            )
          },
          {
            exact: true,
            path: '/actions/invoice-grid/:uuid',
            component: LazyImport(() =>
              import(
                'src/scenes/Actions/Billing/InvoiceGrid' /* webpackChunkName: "InvoiceGrid" */
              )
            )
          },
          {
            exact: true,
            path: '/actions/invoice-details/:uuid',
            component: LazyImport(() =>
              import(
                'src/scenes/Actions/Billing/InvoiceDetails' /* webpackChunkName: "InvoiceDetails" */
              )
            )
          },
          {
            exact: true,
            path: '/actions/telemedicine/video-conference',
            // component: () => checkTelemedicineEnable(LazyImport(() => import('src/scenes/Actions/Telehealth/VideoConference'/* webpackChunkName: "VideoConference" */))())
            component: LazyImport(() =>
              import(
                'src/scenes/Actions/Telehealth/VideoConferenceV2' /* webpackChunkName: "VideoConference" */
              )
            )
          },
          {
            exact: true,
            path: '/actions/urgent-care/video-conference',
            component: () =>
              checkUrgentCareEnable(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/Telehealth/UrgentCarePatientSwitch' /* webpackChunkName: "VideoConference" */
                  )
                )()
              )
          },
          {
            exact: true,
            path: '/actions/waiting-room',
            component: () =>
              checkUrgentCareEnable(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/WaitingRoom' /* webpackChunkName: "WaitingRoom" */
                  )
                )()
              )
          },
          {
            exact: true,
            path: '/actions/telemedicine/appointments',
            component: () =>
              checkTelemedicineEnable(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/Telehealth/Appointments' /* webpackChunkName: "Appointments" */
                  )
                )()
              )
          },
          {
            path: '/actions/broadcast-grid',
            moduleName: MODULE_LIST.broadcast,
            component: checkIfModuleAvailable,
            routes: [
              {
                exact: true,
                path: '/actions/broadcast-grid',
                component: LazyImport(() =>
                  import(
                    'src/scenes/Admin/Broadcast/scenes/BroadcastGrid' /* webpackChunkName: "BroadcastGrid" */
                  )
                )
              },
              {
                path: '/actions/broadcast-grid/add',
                component: LazyImport(() =>
                  import(
                    'src/scenes/Admin/Broadcast/scenes/AddBroadcast' /* webpackChunkName: "AddBroadcast" */
                  )
                )
              },
              {
                path: `/actions/broadcast-grid/edit/:uuid(${UUID_REGEXP})`,
                component: LazyImport(() =>
                  import(
                    'src/scenes/Admin/Broadcast/scenes/EditBroadcast' /* webpackChunkName: "EditBroadcast" */
                  )
                )
              },
              {
                path: '/actions/broadcast-grid/library',
                component: LazyImport(() =>
                  import(
                    'src/scenes/Admin/Broadcast/scenes/BroadcastLibrary' /* webpackChunkName: "BroadcastLibrary" */
                  )
                )
              }
            ]
          },
          {
            path: '/actions/gamification-redemption',
            moduleName: 'GamificationRedemptionManagement',
            component: LazyImport(() =>
              import(
                'src/scenes/Admin/GamificationRedemptionManagement' /* webpackChunkName: "GamificationRedemptionManagement" */
              )
            )
          },
          {
            exact: true,
            path: '/actions/billing-summary',
            component: () =>
              checkBillingDashboards(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/BillingSummary' /* webpackChunkName: "BillingSummary" */
                  )
                )()
              )
          },
          {
            exact: true,
            path: '/actions/billing-details',
            component: () =>
              checkBillingDashboards(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/actions/BillingDetails' /* webpackChunkName: "BillingDetails" */
                  )
                )()
              )
          },
          {
            exact: true,
            path: '/actions/medical-device-management',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/MedicalDeviceManagement' /* webpackChunkName: "MedicalDeviceManagement" */
                  )
                )(),
                'medicalDeviceManagement'
              )
          },
          {
            exact: true,
            path: '/actions/medical-device-ordering',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/MedicalDeviceOrdering' /* webpackChunkName: "MedicalDeviceOrdering" */
                  )
                )(),
                'medicalDevicesAndOrders'
              )
          },
          {
            exact: true,
            path: '/actions/orders',
            component: () =>
              checkPermissionsIsReadOrAdmin(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/actions/orders' /* webpackChunkName: "OrderManagement" */
                  )
                )(),
                [
                  'patientExternalReferralManagement',
                  'referralManagement',
                  'patientPrescriptionManagement',
                  'medication',
                  'medicationManagement',
                  'patientDurableMedicalEquipmentOrderManagement',
                  'medicalDevicesAndOrders',
                  'patientDiagnosticOrderManagement',
                  'diagnosis'
                ]
              )
          },
          {
            exact: true,
            path: '/actions/interventions',
            component: () =>
              checkInterventionsPage(
                LazyImport(() =>
                  import(
                    'src/scenes/Actions/Interventions' /* webpackChunkName: "Interventions" */
                  )
                )()
              )
          },
          {
            path: `/actions/visits`,
            moduleName: MODULE_LIST.soap,
            skipPendingRedirect: true,
            component: checkIfModuleAvailable,
            routes: [
              {
                exact: true,
                path: `/actions/visits`,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Actions/Visits' /* webpackChunkName: "Visits" */
                      )
                    )(),
                    'soapNotes'
                  )
              }
            ]
          },
          // {
          //   exact: true,
          //   path: '/actions/telemedicine/video-conference',
          //   component: LazyImport(() => import('src/scenes/Actions/Telehealth/VideoConference'/* webpackChunkName: "VideoConference" */))
          // },
          // {
          //   exact: true,
          //   path: '/actions/telemedicine/schedule',
          //   component: LazyImport(() => import('src/scenes/Actions/Telehealth/ScheduleGrid'/* webpackChunkName: "ScheduleGrid" */))
          // }
          {
            path: '*',
            component: () => <Redirect to="/not-found" />
          }
        ]
      },
      {
        path: '/configuration',
        component: ({ route }) => renderRoutes(route.routes),
        routes: [
          {
            path: '/configuration/gamification-achievements',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/GamificationAchievementManagement' /* webpackChunkName: "GamificationAchievementManagement" */
                  )
                )(),
                'gamificationSetup'
              )
          },
          {
            path: '/configuration/gamification-tasks',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/GamificationTasksManagement' /* webpackChunkName: "GamificationTasksManagement" */
                  )
                )(),
                'gamificationSetup'
              )
          },
          {
            path: '/configuration/gamification-shop',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/GamificationShopManagement' /* webpackChunkName: "GamificationShopManagement" */
                  )
                )(),
                'gamificationSetup'
              )
          },
          {
            path: '/configuration/case-managers',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/CaseManagers' /* webpackChunkName: "CaseManagers" */
                  )
                )(),
                'caseManagerManagement'
              )
          },
          {
            exact: true,
            path: '/configuration/reference-table',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/ReferenceTable' /* webpackChunkName: "ReferenceTable" */
                  )
                )(),
                'referenceTable'
              )
          },
          {
            path: '/configuration/reference-table/:referenceRoute',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/ReferenceTable/scenes/EditReference' /* webpackChunkName: "EditReference" */
                  )
                )(),
                'referenceTable'
              )
          },
          {
            exact: true,
            path: '/configuration/soap-note-reference-table',
            component: () =>
              checkSoapNoteReferenceTable(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/SoapNoteReferenceTable/ReferenceTable' /* webpackChunkName: "SoapNoteReferenceTable" */
                  )
                )()
              )
          },
          {
            path: '/configuration/soap-note-reference-table/:referenceRoute',
            component: () =>
              checkSoapNoteReferenceTable(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/SoapNoteReferenceTable/EditReference' /* webpackChunkName: "SoapNoteEditReference" */
                  )
                )()
              )
          },
          {
            path: '/configuration/medication-setup',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/MedicationSetup' /* webpackChunkName: "MedicationSetup" */
                  )
                )(),
                'medicationSetup'
              )
          },

          {
            path: '/configuration/clinicians',
            component: ({ route }) => renderRoutes(route.routes),
            routes: [
              {
                path: '/configuration/clinicians/default-schedule',
                exact: true,
                component: () =>
                  checkPremissionsIsWrite(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/Clinicians/DefaultSchedule' /* webpackChunkName: "CliniciansDefaultSchedule" */
                      )
                    )(),
                    'defaultClinicianSchedule'
                  )
              },
              {
                path: '/configuration/clinicians',
                exact: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/Clinicians/List' /* webpackChunkName: "Clinicians" */
                      )
                    )(),
                    'clinicianManagement'
                  )
              },
              {
                path: '/configuration/clinicians/:uuid/clinician-calendar',
                exact: true,
                component: () =>
                  checkPremissionsIsWrite(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/Clinicians/List/Calendar' /* webpackChunkName: "ClinicianCalendar" */
                      )
                    )(),
                    'clinicianManagement'
                  )
              }
            ]
          },
          {
            path: '/configuration/provider-management',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/ProviderManagement' /* webpackChunkName: "ProviderManagement" */
                  )
                )(),
                'providerManagement'
              )
          },
          {
            path: '/configuration/facility-management',
            component: ({ route }) => renderRoutes(route.routes),
            routes: [
              {
                path: '/configuration/facility-management',
                exact: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/FacilityManagement' /* webpackChunkName: "FacilityManagement" */
                      )
                    )(),
                    'facilityManagement'
                  )
              },
              {
                path: `/configuration/facility-management/edit/:facilityUuid(${UUID_REGEXP})`,
                exact: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/portal/admin/facility-management/EditFacility' /* webpackChunkName: "EditFacility" */
                      )
                    )(),
                    'facilityManagement'
                  )
              },
              {
                path: `/configuration/facility-management/floor-edit/:floorUuid(${UUID_REGEXP})`,
                exact: true,
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/FacilityManagement/scenes/EditFloor' /* webpackChunkName: "EditFloor" */
                      )
                    )(),
                    'facilityManagement'
                  )
              }
            ]
          },
          {
            path: '/configuration/organization-management',
            exact: true,
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/admin/organization-management' /* webpackChunkName: "OrganizationManagement" */
                  )
                )(),
                'organizationManagement'
              )
          },
          {
            path: '/configuration/caregivers',
            component: ({ route }) => renderRoutes(route.routes),
            routes: [
              {
                path: `/configuration/caregivers/:caregiverUuid(${UUID_REGEXP})`,
                component: LazyImport(() =>
                  import(
                    'src/scenes/Caregiver' /* webpackChunkName: "Caregiver" */
                  )
                ),
                routes: [
                  {
                    exact: true,
                    path: `/configuration/caregivers/:caregiverUuid(${UUID_REGEXP})/profile`,
                    component: LazyImport(() =>
                      import(
                        'src/scenes/Caregiver/scenes/CaregiverProfile' /* webpackChunkName: "CaregiverProfile" */
                      )
                    )
                  },
                  {
                    exact: true,
                    path: `/configuration/caregivers/:caregiverUuid(${UUID_REGEXP})/access-restrictions`,
                    component: LazyImport(() =>
                      import(
                        'src/scenes/Caregiver/scenes/CaregiverAccessRestrictions' /* webpackChunkName: "CaregiverAccessRestrictions" */
                      )
                    )
                  },
                  {
                    exact: true,
                    path: `/configuration/caregivers/:caregiverUuid(${UUID_REGEXP})/gamification`,
                    moduleName: 'CaregiverGamification',
                    component: checkIfCaregiverModuleAvailable
                  },
                  {
                    path: `/configuration/caregivers/:caregiverUuid(${UUID_REGEXP})`,
                    component: ({ match }) => (
                      <Redirect
                        to={`/configuration/caregivers/${match.params.caregiverUuid}/profile`}
                      />
                    )
                  }
                ]
              },
              {
                path: '/configuration/caregivers',
                component: () =>
                  checkPremissionsIsWrite(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/CaregiverManagement' /* webpackChunkName: "CaregiverManagement" */
                      )
                    )(),
                    'careGiverManagement'
                  )
              }
            ]
          },
          {
            path: '/configuration/referral-source',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/ReferralSource' /* webpackChunkName: "ReferralSource" */
                  )
                )(),
                'referralSourceManagement'
              )
          },
          {
            path: '/configuration/provider-portal',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/ProviderPortal' /* webpackChunkName: "ProviderPortal" */
                  )
                )(),
                'providerPortalUserManagement'
              )
          },
          {
            path: '/configuration/caregiver-shift-upload',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/CaregiverShiftFileUpload' /* webpackChunkName: "CaregiverShiftFileUpload" */
                  )
                )(),
                'careGiverScheduleImport'
              )
          },
          {
            path: '/configuration/quick-message-management',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/QuickMessagesManagement' /* webpackChunkName: "QuickMessagesManagement" */
                  )
                )(),
                'quickMessagesManagement'
              )
          },
          {
            path: '/configuration/agency-case-management',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/AgencyCaseManagement' /* webpackChunkName: "AgencyCaseManagement" */
                  )
                )(),
                'agencyCaseManagerManagement'
              )
          },
          {
            path: '/configuration/agency-marketer',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/AgencyMarketers' /* webpackChunkName: "AgencyMarketers" */
                  )
                )(),
                'agencyMarketerManagement'
              )
          },
          {
            path: '/configuration/care-portal',
            component: LazyImport(() =>
              import(
                'src/scenes/Admin/CarePortal' /* webpackChunkName: "CarePortal" */
              )
            ),
            routes: [
              {
                exact: true,
                path: '/configuration/care-portal/users',
                component: () =>
                  checkPremissionsIsWrite(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/CarePortal/scenes/CarePortalUsers' /* webpackChunkName: "CarePortalUsers" */
                      )
                    )(),
                    'portalUserManagement'
                  )
              },
              {
                exact: true,
                path: '/configuration/care-portal/org-chart',
                component: () =>
                  checkPremissionsIsWrite(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/CarePortal/scenes/CarePortalOrgChart' /* webpackChunkName: "CarePortalOrgChart" */
                      )
                    )(),
                    'portalUserManagement',
                    true
                  )
              }
            ]
          },
          {
            path: '/configuration/measurement-rules-management',
            component: ({ route }) => renderRoutes(route.routes),
            routes: [
              {
                exact: true,
                path: '/configuration/measurement-rules-management',
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/MeasurementRulesManagement' /* webpackChunkName: "MeasurementRulesManagement" */
                      )
                    )(),
                    'measurementRuleManagement'
                  )
              },
              {
                exact: true,
                path: [
                  '/configuration/measurement-rules-management/:companyDivisionUuid/:measurementSettingsUuid',
                  '/configuration/measurement-rules-management/add/:companyDivisionUuid/:measurementUuid'
                ],
                component: () =>
                  checkPermissionsIsRead(
                    LazyImport(() =>
                      import(
                        'src/scenes/Admin/MeasurementRulesManagement/scenes/MeasurementRuleSetup' /* webpackChunkName: "MeasurementRuleSetup" */
                      )
                    )(),
                    'measurementRuleManagement'
                  )
              }
            ]
          },
          {
            path: '/configuration/care-team-management',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/CareTeamManagement' /* webpackChunkName: "CareTeamManagement" */
                  )
                )(),
                'careTeamManagement'
              )
          },
          {
            path: '/configuration/urgent-care-report-management',
            component: () =>
              // checkPremissionsIsWrite(
              LazyImport(() =>
                import(
                  'src/scenes/Admin/UrgentCareReportManagement' /* webpackChunkName: "UrgentCareReportManagement" */
                )
              )() //,
            //   'urgentCareReportManagement'
            // )
          },
          {
            path: '/configuration/notification-rules',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/admin/notification-rules' /* webpackChunkName: "NotificationRules" */
                  )
                )(),
                'notificationRules'
              )
          },
          {
            path: '/configuration/referral-payer',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/ReferralPayer' /* webpackChunkName: "ReferralPayer" */
                  )
                )(),
                'referralPayerManagement'
              )
          },
          {
            path: '/configuration/referral-service-provider',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/ServiceProviderManagement' /* webpackChunkName: "ReferralPayer" */
                  )
                )(),
                'externalReferralServiceProviderManagement'
              )
          },
          {
            path: '/configuration/goal-category',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/GoalCategory' /* webpackChunkName: "GoalCategory" */
                  )
                )(),
                'carePlanManagement'
              )
          },
          {
            path: '/configuration/goal',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import('src/scenes/Admin/Goal' /* webpackChunkName: "Goal" */)
                )(),
                'carePlanManagement'
              )
          },
          {
            path: '/configuration/intervention-category',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/InterventionCategory' /* webpackChunkName: "InterventionCategory" */
                  )
                )(),
                'carePlanManagement'
              )
          },
          {
            path: '/configuration/intervention',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/Intervention' /* webpackChunkName: "Intervention" */
                  )
                )(),
                'carePlanManagement'
              )
          },
          {
            path: '/configuration/disease-management',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/DiseaseManagement' /* webpackChunkName: "ReferralPayer" */
                  )
                )(),
                'diseaseManagement'
              )
          },
          {
            path: '/configuration/strengths-categories',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/StrengthsCategories' /* webpackChunkName: "StrengthsCategories" */
                  )
                )(),
                'strengthsAndBarriersManagement'
              )
          },
          {
            path: '/configuration/strengths',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/Strengths' /* webpackChunkName: "Strengths" */
                  )
                )(),
                'strengthsAndBarriersManagement'
              )
          },
          {
            path: '/configuration/barriers-categories',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/BarriersCategories' /* webpackChunkName: "BarriersCategories" */
                  )
                )(),
                'strengthsAndBarriersManagement'
              )
          },
          {
            path: '/configuration/barriers',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/Admin/Barriers' /* webpackChunkName: "Barriers" */
                  )
                )(),
                'strengthsAndBarriersManagement'
              )
          },
          {
            path: '/configuration/cpt-average-rate',
            component: () =>
              checkPremissionsIsWrite(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/admin/CptAverageRate' /* webpackChunkName: "CptAverageRate" */
                  )
                )(),
                'cptRates'
              )
          },
          {
            path: '*',
            component: () => <Redirect to="/not-found" />
          }
        ]
      },
      {
        path: '/reports',
        component: ({ route }) => renderRoutes(route.routes),
        routes: [
          {
            exact: true,
            path: '/reports/activity-log',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/ReportActivityLog' /* webpackChunkName: "ReportActivityLog" */
                  )
                )(),
                'activityLogReport'
              )
          },
          {
            exact: true,
            path: '/reports/care-plan',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/CarePlanReport' /* webpackChunkName: "CarePlanReport" */
                  )
                )(),
                'carePlanReport'
              )
          },
          {
            exact: true,
            path: '/reports/census-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/CaseManagerCensusV5' /* webpackChunkName: "CaseManagerCensusReport" */
                  )
                )(),
                'censusByCaseManagerReport'
              )
          },
          {
            exact: true,
            path: '/reports/patient-points',
            moduleName: 'PatientPoints',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/PatientPoints' /* webpackChunkName: "PatientPointsReport" */
                  )
                )(),
                'patientPointsReport'
              )
          },
          {
            exact: true,
            path: '/reports/patient-events',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/PatientEventsReport' /* webpackChunkName: "PatientEventsReport" */
                  )
                )(),
                'patientEventsReport'
              )
          },
          {
            exact: true,
            path: '/reports/enrollment',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/EnrollmentReport' /* webpackChunkName: "EnrollmentReport" */
                  )
                )(),
                'enrollmentReport'
              )
          },
          {
            exact: true,
            path: '/reports/telemedicine-appointment',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/TelemedicineAppointment' /* webpackChunkName: "TelemedicineAppointment" */
                  )
                )(),
                'telemedicineAppointmentReport'
              )
          },
          {
            exact: true,
            path: '/reports/email-log',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/EmailLog' /* webpackChunkName: "EmailLogReport" */
                  )
                )(),
                'emailReport'
              )
          },
          {
            exact: true,
            path: '/reports/urgentcare-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/reports/urgent-care-report' /* webpackChunkName: "UrgentCareReport" */
                  )
                )(),
                'urgentCareReport'
              )
          },
          {
            exact: true,
            path: '/reports/out-of-range-rpm',
            component: () =>
              checkPermissionsIsRead(<OutOfRangeRpm />, 'outOfRangeRpmReport')
          },
          {
            exact: true,
            path: '/reports/alert-notes-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/AlertNotesReport' /* webpackChunkName: "AlertNotesReport" */
                  )
                )(),
                'alertNotesReport'
              )
          },
          {
            exact: true,
            path: '/reports/caregivers-shift-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/CaregiverShiftReport' /* webpackChunkName: "CaregiverShiftReport" */
                  )
                )(),
                'careGiverScheduleReport'
              )
          },
          {
            exact: true,
            path: '/reports/patient-measurement-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/reports/PatientMeasurementReport' /* webpackChunkName: "PatientMeasurementReport" */
                  )
                )(),
                'patientMeasurementReport'
              )
          },
          {
            exact: true,
            path: '/reports/rpm-analysis-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/RPMAnalysisReport' /* webpackChunkName: "RPMAnalysisReport" */
                  )
                )(),
                'rpmAnalysis'
              )
          },
          {
            exact: true,
            path: '/reports/monthly-facility-measurements',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import('src/scenes/Reports/MonthlyFacilityMeasurementReport')
                )(),
                'monthlyFacilityMeasurementReport'
              )
          },
          {
            exact: true,
            path: '/reports/program-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/ProgramReport' /* webpackChunkName: "ProgramReport" */
                  )
                )(),
                'programReport'
              )
          },
          {
            exact: true,
            path: '/reports/monitoring-management',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/MonitoringManagement' /* webpackChunkName: "MonitoringManagement" */
                  )
                )(),
                'monitoringDashboard'
              )
          },
          {
            exact: true,
            path: '/reports/patient-external-referral',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/ExternalReferralReport' /* webpackChunkName: "ExternalReferralReport" */
                  )
                )(),
                'externalReferralReport'
              )
          },
          {
            exact: true,
            path: '/reports/length-of-stay',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/LengthOfStay' /* webpackChunkName: "LengthOfStay" */
                  )
                )(),
                'lengthOfStayReport'
              )
          },
          {
            exact: true,
            path: '/reports/transaction-detail',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/TransactionDetailReport' /* webpackChunkName: "TransactionDetailReport" */
                  )
                )(),
                'transactionDetailsReport'
              )
          },
          {
            exact: true,
            path: '/reports/sms-log',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/SMSLog' /* webpackChunkName: "SMSLog" */
                  )
                )(),
                'smsLog'
              )
          },
          {
            exact: true,
            path: '/reports/urgent-care-duty-log',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/reports/urgent-care-statistics' /* webpackChunkName: "UrgentCareDuty" */
                  )
                )(),
                'urgentCareDutyLog'
              )
          },
          {
            exact: true,
            path: '/reports/facility-caregiver',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/FacilityCaregiver' /* webpackChunkName: "FacilityCaregiver" */
                  )
                )(),
                'facilityCaregiverReport'
              )
          },
          {
            exact: true,
            path: '/reports/user-billing-summary',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/UserBillingSummary' /* webpackChunkName: "UserBillingSummary" */
                  )
                )(),
                'employeeBillingSummaryReport'
              )
          },
          {
            exact: true,
            path: '/reports/care-plan-review-date',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/CarePlanReviewDate' /* webpackChunkName: "CarePlanReviewDate" */
                  )
                )(),
                'carePlanReviewDateReport'
              )
          },
          {
            exact: true,
            path: '/reports/integration-logs',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/IntegrationLogs' /* webpackChunkName: "IntegrationLogs" */
                  )
                )(),
                'integrationLogs'
              )
          },
          {
            exact: true,
            path: '/reports/patient-consent',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/reports/PatientConsent' /* webpackChunkName: "PatientConsent" */
                  )
                )(),
                'patientConsentReport'
              )
          },
          {
            exact: true,
            path: '/reports/activities-without-authorizations',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/reports/ActivitiesWithoutAuthorizations' /* webpackChunkName: "ActivitiesWithoutAuthorizations" */
                  )
                )(),
                'activitiesWithoutAuthorizationsReport'
              )
          },
          {
            exact: true,
            path: '/reports/communication-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/CommunicationReport' /* webpackChunkName: "CommunicationReport" */
                  )
                )(),
                'communicationReport'
              )
          },
          {
            exact: true,
            path: '/reports/rpm-billing-exception',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/RPMBillingException' /* webpackChunkName: "RPMBillingException" */
                  )
                )(),
                'rpmBillingException'
              )
          },
          {
            exact: true,
            path: '/reports/daily-facility-measurements',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/DailyFacilityMeasurements' /* webpackChunkName: "DailyFacilityMeasurements" */
                  )
                )(),
                'facilityMeasurements'
              )
          },
          {
            exact: true,
            path: '/reports/clinician-schedule',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/ClinicianSchedule' /* webpackChunkName: "ClinicianSchedule" */
                  )
                )(),
                'clinicianScheduleReport'
              )
          },
          {
            exact: true,
            path: '/reports/care-gap',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/reports/CareGaps' /* webpackChunkName: "CareGaps" */
                  )
                )(),
                'careGapReport'
              )
          },
          {
            exact: true,
            path: '/reports/assessments-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/Reports/AssessmentsReport' /* webpackChunkName: "AssessmentsReport" */
                  )
                )(),
                'assessmentReport'
              )
          },
          {
            exact: true,
            path: '/reports/initial-revisit-report',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/reports/InitialRevisitReport' /* webpackChunkName: "InitialRevisitReport" */
                  )
                )(),
                'initialRevisitReport'
              )
          },
          {
            exact: true,
            path: '/reports/authorization-status',
            component: () =>
              checkPermissionsIsRead(
                LazyImport(() =>
                  import(
                    'src/scenes/portal/reports/AuthorizationStatusReport' /* webpackChunkName: "AuthorizationStatusReport" */
                  )
                )(),
                'authorizationStatusReport'
              )
          },
          {
            path: '*',
            component: () => <Redirect to="/not-found" />
          }
        ]
      },
      // {
      // path: '/tutorials',
      // component: LazyImport(() =>
      //   import('src/components/Tutorials' /* webpackChunkName: "Tutorial" */)
      // )
      // },
      {
        path: '/not-found',
        component: NotFound
      },
      {
        path: '/action-hub',
        component: () =>
          checkIfModuleIsNull(
            LazyImport(() =>
              import('src/scenes/ActionHub' /* webpackChunkName: "ActionHub" */)
            )(),
            'actionHub'
          )
      },
      {
        path: '*',
        component: () => <Redirect to="/not-found" />
      }
    ]
  }
]
